'use client'

import { useCallback, useEffect, useRef } from 'react'

type SoundCue = 'radar' | 'vector' | 'hive' | 'ownership'
const NOTES: Record<SoundCue, number[]> = {
  radar: [660, 880],
  vector: [440, 660],
  hive: [523, 659, 784],
  ownership: [523, 659, 1047],
}
// Browsers keep page audio suspended until an activation-triggering input
// (click, tap or key press). Each one retries the start, so the visitor's
// first interaction anywhere on the page unlocks Scout's voice.
const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'keydown'] as const

export default function useScoutSound() {
  const context = useRef<AudioContext | null>(null)
  const active = useRef(new Map<OscillatorNode, GainNode>())
  // The cue for the line on screen, held until the browser lets audio start.
  const owed = useRef<SoundCue | null>(null)

  const stopSound = useCallback(() => {
    owed.current = null
    active.current.forEach((gain, oscillator) => {
      oscillator.onended = null
      oscillator.stop()
      oscillator.disconnect()
      gain.disconnect()
    })
    active.current.clear()
  }, [])

  const play = useCallback(
    (audio: AudioContext, cue: SoundCue) => {
      const duration = cue === 'ownership' ? 0.22 : 0.18
      try {
        NOTES[cue].forEach((frequency, index) => {
          const oscillator = audio.createOscillator()
          const gain = audio.createGain()
          const start = audio.currentTime + index * 0.11
          oscillator.type = 'sine'
          oscillator.frequency.setValueAtTime(frequency, start)
          gain.gain.setValueAtTime(0, start)
          gain.gain.linearRampToValueAtTime(0.12, start + 0.008)
          gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
          oscillator.connect(gain).connect(audio.destination)
          oscillator.onended = () => {
            active.current.delete(oscillator)
            oscillator.disconnect()
            gain.disconnect()
          }
          oscillator.start(start)
          active.current.set(oscillator, gain)
          oscillator.stop(start + duration + 0.01)
        })
      } catch {
        stopSound()
      }
    },
    [stopSound]
  )

  const playSound = useCallback(
    (cue: SoundCue) => {
      const audio = context.current
      if (!audio || audio.state === 'closed') return
      stopSound()
      if (audio.state === 'running') return play(audio, cue)
      // Blocked or interrupted: owe the cue and play it once audio starts.
      owed.current = cue
      void audio.resume().catch(() => {})
    },
    [play, stopSound]
  )

  // The visible tour and every UNLOCK_EVENTS input start audio here, so a
  // gesture made before the hero is on screen still creates and unlocks it.
  const enableSound = useCallback(() => {
    if (!context.current) {
      let created: AudioContext
      try {
        created = new AudioContext()
      } catch {
        return // No Web Audio support: the tour runs silently.
      }
      // Starts on load where the browser allows it; otherwise the owed cue
      // plays the moment an input unlocks audio.
      created.onstatechange = () => {
        const cue = owed.current
        if (context.current !== created || created.state !== 'running' || !cue)
          return
        owed.current = null
        play(created, cue)
      }
      context.current = created
    }
    const audio = context.current
    if (audio.state !== 'running' && audio.state !== 'closed')
      void audio.resume().catch(() => {})
  }, [play])

  useEffect(() => {
    UNLOCK_EVENTS.forEach(type =>
      window.addEventListener(type, enableSound, {
        capture: true,
        passive: true,
      })
    )
    return () => {
      UNLOCK_EVENTS.forEach(type =>
        window.removeEventListener(type, enableSound, { capture: true })
      )
      stopSound()
      const audio = context.current
      context.current = null
      if (audio && audio.state !== 'closed') void audio.close().catch(() => {})
    }
  }, [enableSound, stopSound])

  return { enableSound, playSound, stopSound }
}
